import type { CategoryContent } from '../../row.js';

/** Literature & Poetry, second chunk. Correct option first; rotated at build. */
export const MORE_LITERATURE: CategoryContent = {
  msc: [
    [
      'Wat is "free indirect discourse" (vrije indirecte rede)?',
      [
        'Vertelling in de derde persoon die de spreekwijze en het perspectief van een personage overneemt zonder aanhalingstekens',
        'Dialoog die wordt weergegeven zonder toeschrijving aan een spreker, zodat de lezer moet afleiden wie er praat',
        'Een verteller in de eerste persoon die de lezer rechtstreeks toespreekt',
        'Innerlijke monoloog weergegeven zonder interpunctie of syntaxis',
      ],
      0,
      'Austen krijgt meestal de eer dit in het Engels tot volmaaktheid te hebben gebracht. Onleesbare innerlijke monoloog zonder interpunctie ligt dichter bij stream of consciousness, een later en ander procedé.',
    ],
    [
      'Welk metrum domineert het Engelse dramatische vers uit de Elizabethaanse periode?',
      ['Jambische pentameter', 'Dactylische hexameter', 'Trochaïsche tetrameter', 'Anapestische trimeter'],
      0,
      'Blank verse - ongerijmde jambische pentameter - die Surrey introduceerde en Marlowe spreektaal-waardig maakte.',
    ],
    [
      'Hoe is het Petrarcaanse sonnet ingedeeld?',
      [
        'Een octaaf en een sextet, met de wending daartussen',
        'Drie kwatrijnen en een afsluitend rijmpaar',
        'Twee zeven-regelige strofen met een gedeelde refrein',
        'Veertien ongerijmde regels zonder interne indeling',
      ],
      0,
      'De vorm met drie kwatrijnen en een afsluitend rijmpaar is de Engelse of Shakespeariaanse vorm, die de wending op een andere plaats legt.',
    ],
    [
      'Wat duidt de term "bildungsroman" aan?',
      [
        'Een roman die de vorming van zijn protagonist volgt van jeugd tot volwassenheid',
        'Een roman die volledig is opgebouwd uit brieven tussen personages',
        'Een roman die zich afspeelt binnen één dag in één stad',
        'Een roman waarvan de verteller aan het einde onbetrouwbaar blijkt',
      ],
      0,
      'Wilhelm Meister is het schoolvoorbeeld. Een roman opgebouwd uit brieven is de epistolaire vorm, en beide werden in dezelfde periode geschreven.',
    ],
    [
      'Wie schreef "Het Verhaal van Genji"?',
      [
        'Murasaki Shikibu',
        'Sei Shonagon, wier Kussenboek tot hetzelfde hof behoort',
        'Ki no Tsurayuki, samensteller van de Kokinshu',
        'Yoshida Kenko, auteur van Overpeinzingen in Ledigheid',
      ],
      0,
      'Murasaki Shikibu schreef het begin elfde eeuw; het wordt vaak de eerste psychologische roman genoemd. Sei Shonagon, Ki no Tsurayuki en Yoshida Kenko behoren allen tot dezelfde Heian- en vroeg-middeleeuwse Japanse hoftraditie, waardoor de namen makkelijk te verwarren zijn zonder het specifieke werk van elk te kennen.',
    ],
    [
      'Wat is prosopopeia?',
      [
        'Een stijlfiguur waarin een afwezig of levenloos ding een stem krijgt',
        'De vervanging van een eigenschap door de zaak zelf',
        'Een toespraak tot een afwezige persoon of abstractie',
        'De herhaling van een woord aan het begin van opeenvolgende zinsdelen',
      ],
      0,
      'Apostrofe is de toespraak, metonymie de vervanging, anafoor de herhaling - alle drie worden er vaak mee verward.',
    ],
    [
      'Bij welke stroming wordt César Vallejo gerekend?',
      [
        'De Latijns-Amerikaanse avant-gardepoëzie, verwant aan maar te onderscheiden van het surrealisme',
        'De Spaanse Generatie van 1927, samen met Lorca, Alberti en Cernuda in Madrid',
        'Het Braziliaanse modernismo van de jaren 1920',
        'Het Chileense Creacionismo van Huidobro',
      ],
      0,
      'Trilce (1922) gaat het surrealistische manifest vooraf en breekt de Spaanse syntaxis gewelddadiger dan de stroming zelf ooit deed.',
    ],
    [
      'Wat is het Russisch-formalistische begrip ostranenie?',
      [
        'Vervreemding: kunst maakt het gewone vreemd zodat het opnieuw waargenomen kan worden',
        'De vervreemding van de lezer door een onbetrouwbare verteller',
        'De scheiding tussen het verhaalmateriaal en de ordening ervan in de vertelling',
        'Het vervreemdingseffect dat wordt bereikt door de dramatische illusie te doorbreken',
      ],
      0,
      'Sjklovski, 1917. Het onderscheid tussen verhaalmateriaal en ordening is fabula en syuzhet, en het vervreemdingseffect is van Brecht.',
    ],
    [
      'Door wie werd de beweging van de "Négritude" voornamelijk gesticht?',
      [
        'Aimé Césaire, Léopold Sédar Senghor en Léon Damas',
        'Chinua Achebe en Wole Soyinka',
        'Frantz Fanon en Albert Memmi',
        'Claude McKay en Langston Hughes',
      ],
      0,
      'Parijs in de jaren 1930. De schrijvers van de Harlem Renaissance beïnvloedden de beweging, Fanon leverde kritiek erop, en de Engelstalige Afrikanen kwamen later en stonden er sceptisch tegenover.',
    ],
    [
      'Wat beschrijft "in medias res"?',
      [
        'Een verhaal beginnen midden in de handeling in plaats van bij het begin',
        'Een verhaal dat aan het slot terugkeert naar zijn openingsscène',
        'Een uitweiding die de hoofdhandeling onderbreekt',
        'Een subplot dat de structuur van de hoofdplot weerspiegelt',
      ],
      0,
      'Het voorschrift van Horatius, en zowel de Ilias als de Odyssee doen het al - wat precies is waarom hij het kon voorschrijven.',
    ],
  ],
  phd: [
    [
      'Wat betoogt Auerbachs "Mimesis" over de bijbelse en Homerische vertelling?',
      [
        'Homerische stijl veruitwendigt alles, terwijl de bijbelse vertelling "geladen is met achtergrond"',
        'Beide tradities delen een paratactische syntaxis die orale compositie weerspiegelt',
        'De bijbelse vertelling houdt zich meer bezig met individuele psychologie dan het Griekse epos',
        'De Homerische vertelling onderschikt beschrijving aan plot op een manier die de Schrift niet doet',
      ],
      0,
      'Het hoofdstuk over Odysseus\' litteken. De claim over vooropstelling versus diepte doet al het werk, en zij zette een hele methode in beweging.',
    ],
    [
      'Aan wie wordt het begrip "de dood van de auteur" toegeschreven, en wat beweert het?',
      [
        'Barthes: de betekenis van een tekst wordt niet vastgelegd door de bedoeling van de auteur',
        'Foucault: auteurschap is een historisch bepaalde functie van het discours',
        'Derrida: geen tekst heeft een bepaalbare betekenis',
        'Wimsatt en Beardsley: een beroep op intentie is een kritische denkfout',
      ],
      0,
      'Alle vier zijn reële posities die vaak met elkaar worden verward. Foucaults "Wat is een auteur?" is het antwoord op Barthes, geen herhaling ervan.',
    ],
    [
      'Wat is een "cento"?',
      [
        'Een gedicht dat volledig is samengesteld uit regels afkomstig uit andere gedichten',
        'Een verstvorm van honderd regels van Provençaalse oorsprong',
        'Een gedicht waarin elke regel begint met opeenvolgende letters van het alfabet',
        'Een commentaar geschreven in de marge van een klassieke tekst',
      ],
      0,
      'Laat-antieke dichters bouwden er hele christelijke verhalen mee uit Vergilius. Het is ook waarom de vorm steeds opnieuw als avant-garde wordt herontdekt.',
    ],
    [
      'Waar verwijst het Sanskrit-begrip rasa naar?',
      [
        'De esthetische emotie die door een werk bij een publiek wordt teweeggebracht',
        'De metrische structuur van een versregel in de klassieke prosodie',
        'De morele instructie die een drama verplicht is over te brengen',
        'De formele indeling van een stuk in akten en scènes',
      ],
      0,
      'Abhinavaguptas verslag van hoe het wordt teweeggebracht en ontvangen is de verfijnde versie, en het gaat elke Europese receptietheorie ver vooraf.',
    ],
    [
      'Wat onderscheidt het picareske genre van de bildungsroman?',
      [
        'De picaro is episodisch en verandert niet door ervaring; de protagonist van de bildungsroman wordt erdoor gevormd',
        'Het picareske wordt altijd verteld in de derde persoon en nooit in de eerste, wat het onderwerp ook is',
        'Het picareske beperkt zich tot de Spaanse literatuur van de zestiende eeuw',
        'Het picareske mist volgens de conventie een ik-verteller',
      ],
      0,
      'Lazarillo de Tormes is de oorsprong. Het ontbreken van ontwikkeling is structureel, niet toevallig - het gaat om een overzicht van de samenleving, niet om een leven.',
    ],
    [
      'Wat is het betoog van Bachtins "Rabelais en zijn wereld"?',
      [
        'Carnaval keert hiërarchie om via het groteske lichaam',
        'De roman ontstond uit het epos door een geleidelijke verslapping van de metrische dwang',
        'Rabelais\' obsceniteit is een verhulde kritiek op specifieke kerkelijke figuren',
        'De populaire feestcultuur stamt af van overgebleven heidense landbouwrituelen',
      ],
      0,
      'Geschreven in de jaren 1930, gepubliceerd in 1965. Of carnaval werkelijk subversief is of een veiligheidsklep, is het debat dat het in gang zette.',
    ],
    [
      'Waardoor wordt de traditie van de "Menippeïsche satire" gekenmerkt?',
      [
        'Gemengd proza en vers, fantastische decors',
        'Verzinvectief gericht tegen bij naam genoemde tijdgenoten',
        'Dialoog tussen een wijze gesprekspartner en een naïeve vraagsteller',
        'Allegorische vertelling met een strikte één-op-één-overeenkomst',
      ],
      0,
      'Lucianus, Petronius, en via hen Swift en Sterne. Frye en Bachtin gebruiken de categorie beide voor heel verschillende doeleinden.',
    ],
    [
      'Wat bedoelt Genette met "paratext"?',
      [
        'Het apparaat dat een tekst omringt',
        'De intertekstuele verwijzingen die een werk naar eerdere werken maakt',
        'De variantlezingen die in een kritisch apparaat zijn opgenomen',
        'De commentaartraditie die zich rond een canoniek werk opbouwt',
      ],
      0,
      'Seuils, 1987. Het maakte van het fysieke boek een legitiem object van poëtica, niet slechts een zaak voor bibliografen.',
    ],
    [
      'Waarin lag het belang van het Chinese "achtdelige essay" (bagu wen)?',
      [
        'De strikt voorgeschreven prozavorm die verplicht was bij de keizerlijke ambtenarenexamens',
        'Een klassieke versvorm van acht parallelle koppels, verplicht in het poëziegedeelte van het examen',
        'Een commentaargenre dat aan de confucianistische klassieken verbonden was',
        'Een dramatische structuur gebruikt in het theater van de Yuan-dynastie',
      ],
      0,
      'De afschaffing ervan in 1905 beëindigde een systeem van duizend jaar oud, en het blijft in het Chinees een symbool voor verstikkend formalisme.',
    ],
    [
      'Wat is "aemulatio" in de renaissancistische poëtica?',
      [
        'Rivaliserende imitatie die haar model probeert te overtreffen in plaats van slechts te reproduceren',
        'De letterlijke vertaling van een klassieke tekst in de volkstaal',
        'De toeschrijving van een nieuw werk aan een klassieke auteur',
        'De verzameling van gemeenplaatsen uit erkende autoriteiten',
      ],
      0,
      'Imitatio met ambitie. Het is waarom "afgeleid" een anachronistisch verwijt is om zestiende-eeuwse poëzie te maken.',
    ],
  ],
  professor: [
    [
      'Wat stelt Morettis "distant reading" voor, en wat is het gangbare tegenwerpen?',
      [
        'Kwantitatieve analyse van grote corpora',
        'Nauwkeurige aandacht voor niet-canonieke teksten, wat men bekritiseert als verwatering van de canon',
        'Het lezen van vertalingen in plaats van originelen, bekritiseerd op filologische gronden',
        'Analyse van uitgeversgegevens in plaats van teksten, bekritiseerd als boekgeschiedenis',
      ],
      0,
      'De interessante versie van het tegenwerpen is niet anti-kwantitatief: het is dat de geanalyseerde eenheden zelf al interpretatief zijn.',
    ],
    [
      'Waarop richtte de "New Bibliography" van Greg en Bowers zich?',
      [
        'Het reconstrueren van de definitieve intenties van een auteur via eclectische editie van getuigen',
        'Het weergeven van de vroegst overgeleverde gedrukte tekst zonder emendatie',
        'Het registreren van elke variant zonder daartussen te oordelen',
        'Het vaststellen van de sociale omstandigheden waaronder een tekst tot stand kwam',
      ],
      0,
      'De kritiek van McGann en McKenzie op de "gesocialiseerde tekst" is de reactie, en dat is waarom moderne edities veel vaker documentair van aard zijn.',
    ],
    [
      'Wat is het belang van de Nag Hammadi-codices voor de studie van de vroegchristelijke literatuur?',
      [
        'Ze bewaren Koptische vertalingen van gnostische teksten die voorheen vooral bekend waren via vijandige samenvattingen',
        'Ze bevatten de vroegst bekende manuscripten van de canonieke evangeliën',
        'Ze bewaren een Hebreeuws origineel van een Nieuwtestamentisch boek',
        'Ze leggen de handelingen van het Concilie van Nicea vast, inclusief de leerstellige debatten die de overgeleverde canons weglaten',
      ],
      0,
      'Gevonden in 1945. Het Evangelie van Thomas is degene die veranderde hoe de traditie van uitspraken wordt gereconstrueerd.',
    ],
    [
      'Wat beweert de "oraal-formulaïsche" theorie van Parry en Lord?',
      [
        'Epische dictie is opgebouwd uit metrische formules die compositie tijdens de voordracht mogelijk maken',
        'Epossen werden woordelijk gememoriseerd en zonder variatie overgeleverd',
        'Geschreven epos stamt in elke traditie af van één verloren oraal origineel',
        'Formulaïsche herhaling is een teken van late scribale interpolatie',
      ],
      0,
      'Parry\'s Homerische analyse werd door Lord getoetst bij Zuid-Slavische zangers. Het herformuleerde "was Homerus één dichter" als de verkeerde vraag.',
    ],
    [
      'Wat betoogt Spivak in "Can the Subaltern Speak?"?',
      [
        'Dat representatiestructuren subalterne spraak onmogelijk maken',
        'Dat subalterne getuigenissen altijd beschikbaar zijn als archieven tegen de haren in worden gelezen',
        'Dat koloniale archieven geen bruikbare informatie over ondergeschikte groepen bevatten',
        'Dat subalterne studies de tekstuele methode zouden moeten opgeven voor mondelinge geschiedenis',
      ],
      0,
      'Het essay wordt vaak aangehaald als een botte ontkenning, wat het niet is - het betoog gaat over de voorwaarden van verstaanbaarheid.',
    ],
    [
      'Wat wordt de "ecokritiek" doorgaans toegeschreven als bijdrage aan de literatuurstudie?',
      [
        'Aandacht voor de representatie van niet-menselijke natuur',
        'Een methode om teksten te dateren aan de hand van verwijzingen naar klimaatgebeurtenissen',
        'Een kwantitatieve index van natuurbeelden door de perioden heen',
        'Een vereiste dat kritiek in dienst staat van milieuactivisme',
      ],
      0,
      'Het sterkste werk erbinnen - Buell, Morton - gaat vooral over wat de pastorale conventie onmogelijk maakt om op te merken.',
    ],
    [
      'Om welk probleem draait de recente herleving van het begrip "wereldliteratuur"?',
      [
        'Hoe over talen heen te lezen zonder verschil te vervlakken of terug te vallen op een metropolitane canon',
        'Of vertaling in beginsel mogelijk is tussen onverwante taalfamilies',
        'Of de roman een universele of een Europese vorm is',
        'Hoe een eenduidige chronologie vast te stellen over onvergelijkbare tradities heen',
      ],
      0,
      'Damrosch, Casanova en Apter geven heel verschillende antwoorden, en Apters "onvertaalbaarheid" is het scherpste tegenwerpen tegen het hele project.',
    ],
    [
      'Wat bedoelt de "tekstkritiek van het Nieuwe Testament" met een eclectische tekst?',
      [
        'Een gereconstrueerde tekst die lezingen uit verschillende manuscripten overneemt op interne en externe gronden',
        'Een tekst die overal één manuscript volgt en alleen wordt aangepast waar het onleesbaar is',
        'Een tekst die overal de meerderheidslezing afdrukt waar manuscripten van elkaar afwijken',
        'Een tekst die alle varianten opsomt zonder een voorkeurslezing af te drukken',
      ],
      0,
      'De Nestle-Aland-editie. Zowel meerderheidstekst- als één-manuscript-benaderingen bestaan en hebben allebei aanhangers, wat is waarom de keuze een methodologisch engagement is.',
    ],
    [
      'Wat houdt de Sanskrit-theorie van "dhvani" van Anandavardhana in?',
      [
        'Poëtische betekenis werkt door suggestie, voorbij de letterlijke en figuurlijke betekenissen',
        'Poëzie onderscheidt zich van proza alleen door metrische regelmaat',
        'Het doel van poëzie is onderricht verhuld in genot',
        'Figuurlijke taal is ornamenteel en los te maken van de betekenis',
      ],
      0,
      'Suggestie als de ziel van de poëzie, tegenover de oudere op ornament gebaseerde scholen - en een positie zonder nauw Europees tegenhanger vóór de symbolisten.',
    ],
    [
      'Wat is het betoog van Erich Auerbachs essay over "figura"?',
      [
        'Dat figurale interpretatie historische gebeurtenissen leest als voorafspiegelingen van latere, waarbij beide reëel blijven',
        'Dat allegorie de letterlijke betekenis oplost ten gunste van de geestelijke',
        'Dat typologie een middeleeuwse uitvinding was zonder patristisch precedent',
        'Dat figuurlijke taal in de Schrift altijd ornamenteel is, en los te maken van de leerinhoud die zij versiert',
      ],
      0,
      'Het onderscheid met allegorie is het dragende deel: bij figurale lezing blijven beide termen historisch reëel, wat Dante mogelijk maakt.',
    ],
  ],
};
